<template>
  <div class="row" :class="getDayClass">
    <div class="col-md-8">
      <div v-if="oncall === false || oncall.length > 0" class="oncall">
        On-call:
        <span v-if="oncall === false">
          <font-awesome-icon icon="spinner" spin pulse />
        </span>
        <template v-else>
          <span
            v-for="(oncallEntry, oncallIndex) in getOncallUsersForDayIndex"
            :key="oncallIndex"
            data-dd-privacy="mask"
          >
            <user-display-resolver
              :user-id="oncallEntry.userId"
              :display-name="oncallEntry.displayName"
            />
            {{ oncallEntry.phone }}
          </span>
        </template>
      </div>
      <p v-if="currentShiftsForUserId.length == 0" class="no-current">
        No shifts are currently assigned to you on this rota.
      </p>
      <div>
        <div
          class="shifts"
          :class="{
            today: rotaDateIsToday(rota.startDate, dayIndex),
          }"
        >
          <template v-for="(shift, index) in shiftsForUserId" :key="index">
            <div
              :id="`shift-${shift.id}`"
              class="shift"
              :class="{
                [`type-${getShiftAsignmentType(shift)}`]: 1,
              }"
            >
              <rota-shift-with-cover-request
                :rota-type="rota.type"
                :rota-status="rota.status"
                :rota-id="rota.id"
                :shift="shift"
                :activities="activities"
                :location="rota.location"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="col-md-4 activities">
      <template v-if="activities !== false">
        <div
          v-if="activitiesForDayIndex.length > 0"
          class="unassigned-activities d-flex"
        >
          <h3>Other Activities</h3>
          <div class="items">
            <template v-if="locationClients.length > 0">
              <rota-activity-card
                v-for="(activity, index) in activitiesForDayIndex"
                :id="activity.id"
                :key="index"
                :activity="activity"
                :show-shifts="false"
                :show-end-time="false"
                class="mb-1"
              />
            </template>
            <template v-else>
              <p class="card-text placeholder-glow">
                <span class="placeholder col-12 py-3"></span>
              </p>
              <p class="card-text placeholder-glow">
                <span class="placeholder col-12 py-3"></span>
              </p>
            </template>
          </div>
        </div>
        <div v-else class="unassigned-activities d-none d-md-flex">
          <h3>Other Activities</h3>
          <div class="items">
            <div class="text-center text-muted">No activities</div>
          </div>
        </div>
      </template>
      <div v-else class="unassigned-activities">
        <p class="card-text placeholder-glow">
          <span class="placeholder col-12 py-3"></span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import filter from "lodash/filter";
import find from "lodash/find";
import findLast from "lodash/findLast";
import get from "lodash/get";
import {
  addDays,
  isSameDay,
  isToday,
  isFuture,
  setMilliseconds,
} from "date-fns";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import RotaActivityCard from "@/components/activity/RotaActivityCard.vue";
import UserDisplayResolver from "@/components/UserDisplayResolver.vue";
import RotaShiftWithCoverRequest from "@/components/rota/shift/RotaShiftWithCoverRequest.vue";

import { getActivitiesForRotaDay } from "@//lib/rota/activity-functions";
import {
  formatDateAsDayWithSuffixShortMonth,
  formatTimeHoursMins,
  rotaDateIsToday,
} from "@/lib/date-functions";
import {
  getShiftsForUserId,
  filterShiftsForDayIndexRange,
} from "@/lib/rota/shift-functions";

export default {
  name: "RotaUserAgendaViewDay",
  components: {
    UserDisplayResolver,
    RotaActivityCard,
    RotaShiftWithCoverRequest,
  },
  props: {
    rota: {
      type: Object,
      default() {
        return {};
      },
    },
    rotaShiftsForUserOrVolunteering: {
      type: Array,
      default() {
        return [];
      },
    },
    rotaUsers: {
      type: Array,
      default() {
        return [];
      },
    },
    dayIndex: {
      type: Number,
      default() {
        return 0;
      },
    },
    activities: {
      type: [Array, Boolean],
      default() {
        return false;
      },
    },
    activitiesNotAssignedWithoutSupport: {
      type: Array,
      default() {
        return [];
      },
    },
    oncall: {
      type: [Array, Boolean],
      default() {
        return false;
      },
    },
    rotaAgencies: {
      type: Array,
      default() {
        return [];
      },
    },
    locationClients: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  setup(props) {
    const authStore = useAuthStore();

    const shiftsForDayIndex = computed(() => {
      return filterShiftsForDayIndexRange(
        props.rotaShiftsForUserOrVolunteering,
        props.rota.startDate,
        props.dayIndex,
        props.dayIndex,
      );
    });

    const activitiesForDayIndex = computed(() =>
      getActivitiesForRotaDay(
        props.activitiesNotAssignedWithoutSupport,
        props.rota.startDate,
        props.dayIndex,
      ),
    );

    const shiftsForUserId = computed(() => {
      return getShiftsForUserId(
        shiftsForDayIndex.value,
        authStore.isAgencyUser ? authStore.user.agencyId : authStore.user.id,
        undefined,
        undefined,
        undefined,
        ["current", "sick", "holiday", "old"],
        true, // we want to include volunteered shifts
      );
    });

    const currentShiftsForUserId = computed(() => {
      return getShiftsForUserId(
        shiftsForDayIndex.value,
        authStore.isAgencyUser ? authStore.user.agencyId : authStore.user.id,
        undefined,
        undefined,
        undefined,
        ["current"],
        false, // we're only interested in CURRENT assignemnts
      );
    });

    const dayIsToday = computed(() => {
      const d = addDays(
        setMilliseconds(props.rota.startDate, 0),
        props.dayIndex,
      );
      return isToday(d);
    });

    return {
      authStore,
      shiftsForDayIndex,
      activitiesForDayIndex,
      getDayClass: computed(() => {
        return {
          today: rotaDateIsToday(props.rota.startDate, props.dayIndex),
          "no-current": currentShiftsForUserId.value.length === 0,
        };
      }),
      getShiftAsignmentType: (shift) => {
        let match;
        if (authStore.isAgencyUser) {
          match = findLast(shift.assignedTo, {
            agencyId: authStore.user.agencyId,
          });
        } else {
          match = findLast(shift.assignedTo, {
            userId: authStore.user.id,
          });
        }
        return match ? match.type : "";
      },
      getOncallUsersForDayIndex: computed(() => {
        if (props.oncall === false) {
          return {};
        }
        const onCallPeople = filter(props.oncall, (oncall) =>
          isSameDay(oncall.date, addDays(props.rota.startDate, props.dayIndex)),
        );
        return onCallPeople.map((oncall) => {
          const user = find(props.rotaUsers, { userId: oncall.userId });
          return {
            ...user,
            ...oncall,
            phone: get(user, "phone", ""),
          };
        });
      }),
      dayIsCurrent: computed(() => {
        const d = addDays(
          setMilliseconds(props.rota.startDate, 0),
          props.dayIndex,
        );
        return isToday(d) || isFuture(d);
      }),
      dayIsToday,
      getShiftsForUserId,
      shiftsForUserId,
      currentShiftsForUserId,
      formatDateAsDayWithSuffixShortMonth,
      formatTimeHoursMins,
      rotaDateIsToday,
    };
  },
};
</script>
